import { CheckCircleIcon, InfoIcon, WarningCircleIcon } from '@phosphor-icons/react'
import type { ReactNode } from 'react'
import { cn } from 'app/lib/utils'

export type AlertTone = `ok` | `info` | `warn` | `error`

const TONE_STYLES: Record<AlertTone, string> = {
  ok: `border-emerald-200 bg-emerald-50 text-emerald-700`,
  info: `border-default bg-surface-subtle text-subtle`,
  warn: `border-amber-200 bg-amber-50 text-amber-800`,
  error: `border-red-200 bg-red-50 text-red-700`,
}

const TONE_ICON: Record<AlertTone, typeof CheckCircleIcon> = {
  ok: CheckCircleIcon,
  info: InfoIcon,
  warn: WarningCircleIcon,
  error: WarningCircleIcon,
}

/** One tone-driven status strip for the inspector — replaces the ad-hoc issue / graph / engine-gap /
 *  object-requirement boxes so "state" reads consistently and distinctly from section content. `lead`
 *  is an optional bold lead-in ("Acts on the contact."). */
export function Alert({ tone, lead, children }: { tone: AlertTone; lead?: string; children?: ReactNode }) {
  const Icon = TONE_ICON[tone]
  return (
    <div className={cn(`flex items-start gap-1.5 rounded-md border px-3 py-2 text-[11px]`, TONE_STYLES[tone])}>
      <Icon size={14} weight="bold" className="mt-px shrink-0" />
      <span>
        {lead && <span className="font-semibold">{lead}. </span>}
        {children}
      </span>
    </div>
  )
}
